import { getVariant, Box, InputProps } from '@apia/theme';
import { isChild, useCombinedRefs } from '@apia/util';
import React from 'react';
import { TIconName, TIconType } from '@apia/icons';
import { IconButton } from './buttons';
import ReactInputMask from 'react-input-mask';

export interface IField {
  className?: string;
  disabled?: boolean;
  isLoading?: boolean;
  readOnly?: boolean;
}

export interface IIconInput extends IField {
  additionalButtons?: React.ReactNode;
  additionalButtonsPosition?: 'before' | 'after';
  buttonProps?: Partial<React.ComponentProps<typeof IconButton>>;
  buttonRef?: React.RefObject<HTMLButtonElement>;
  hideButton?: boolean;
  icon?: TIconType | TIconName;
  inputProps?: Partial<InputProps>;
  inputRef?: React.RefObject<HTMLInputElement>;
  mask?: string;
  onClick?: (ev: React.MouseEvent) => void;
  onBlur?: React.FocusEventHandler;
  [key: string]: unknown;
}

export const IconInput = ({
  additionalButtons,
  additionalButtonsPosition = 'before',
  buttonProps,
  buttonRef,
  className,
  disabled,
  hideButton,
  icon = 'Search',
  inputProps,
  inputRef,
  isLoading,
  mask,
  onClick,
  onBlur,
  readOnly,
  ...rest
}: IIconInput) => {
  const { sx: inputSx, ...actualInputProps } = inputProps ?? {};
  const innerButtonRef = React.useRef<HTMLButtonElement>(null);
  const assignButtonRef = useCombinedRefs(buttonRef, innerButtonRef);

  const parentRef = React.useRef<HTMLElement | null>(null);
  const handleBlur = React.useCallback(
    (ev: React.FocusEvent) => {
      if (
        onBlur &&
        (!ev.relatedTarget ||
          (ev.relatedTarget &&
            !isChild(
              ev.relatedTarget as HTMLElement,
              (current) => current === parentRef.current,
            )))
      ) {
        Object.assign(ev.target, {
          value: ev.target
            .closest<HTMLElement>('.iconInput')!
            .querySelector<HTMLInputElement>('input')!.value,
        });
        onBlur(ev);
      }
    },
    [onBlur],
  );

  return (
    <Box
      {...getVariant('forms.iconInput')}
      ref={parentRef}
      onBlur={handleBlur}
      className={`${className ?? ''} iconInput`}
    >
      <ReactInputMask
        mask={mask ?? ''}
        disabled={isLoading || disabled}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ref={inputRef as any}
        readOnly={readOnly}
        sx={{ variant: 'forms.input', ...inputSx }}
        data-variant="forms.input"
        {...rest}
        {...actualInputProps}
        maxLength={mask ? undefined : inputProps?.maxLength}
      />
      {additionalButtonsPosition === 'before' && additionalButtons}
      {!hideButton && (
        <IconButton
          onClick={onClick}
          icon={icon}
          disabled={disabled}
          isLoading={isLoading}
          ref={assignButtonRef as React.Ref<HTMLButtonElement>}
          {...buttonProps}
        />
      )}
      {additionalButtonsPosition === 'after' && additionalButtons}
    </Box>
  );
};
